import { IMPORT_MAX_RECORDS } from "@bookdental/schemas";

/**
 * Envia a planilha em lotes de `IMPORT_MAX_RECORDS`.
 *
 * O servidor limita registros POR REQUISIÇÃO porque roda até 4 consultas de deduplicação por linha
 * — 200 mil linhas num único POST seguravam transação e memória. O limite não é da planilha: quem
 * migra de outro sistema tem 20, 30 mil pacientes, e recusar o arquivo seria trocar um problema de
 * infraestrutura por um problema do usuário. Aqui a planilha inteira passa, só que fatiada.
 *
 * Cada lote vira um ImportBatch próprio no servidor (é o escopo de "desfazer importação"), então
 * uma planilha grande aparece como mais de uma entrada no histórico.
 */

export type ProgressoImport = { enviados: number; total: number; lote: number; lotes: number };

/** Erro que carrega o que JÁ foi gravado antes da falha — importação parcial não pode parecer nada.
 *  `parcial` é null quando nem o primeiro lote completou (aí não há nada no banco). */
export class ErroImportacaoParcial<T = unknown> extends Error {
  constructor(
    readonly parcial: T | null,
    readonly causaOriginal: unknown,
  ) {
    super(causaOriginal instanceof Error ? causaOriginal.message : "Erro na importação");
    this.name = "ErroImportacaoParcial";
  }
}

/**
 * Combina o resultado de dois lotes sem conhecer a forma: número soma, lista concatena (com teto,
 * igual ao que o servidor já aplica por lote), o resto fica com o valor mais recente. Assim serve
 * tanto pro resultado de pacientes quanto pro de consultas, que tem campos próprios.
 */
function combinar<T extends Record<string, any>>(acc: T, novo: T): T {
  const saida: Record<string, any> = { ...acc };
  for (const [chave, valor] of Object.entries(novo)) {
    const anterior = acc[chave];
    if (typeof valor === "number" && typeof anterior === "number") saida[chave] = anterior + valor;
    else if (Array.isArray(valor) && Array.isArray(anterior)) saida[chave] = [...anterior, ...valor].slice(0, 10);
    else saida[chave] = valor;
  }
  return saida as T;
}

export function contarLotes(total: number): number {
  return Math.max(1, Math.ceil(total / IMPORT_MAX_RECORDS));
}

export async function importarEmLotes<TRegistro, TResultado extends Record<string, any>>(
  records: TRegistro[],
  enviar: (lote: TRegistro[]) => Promise<TResultado>,
  onProgresso?: (p: ProgressoImport) => void,
): Promise<TResultado> {
  const lotes = contarLotes(records.length);
  let acumulado: TResultado | null = null;

  for (let i = 0; i < lotes; i++) {
    const inicio = i * IMPORT_MAX_RECORDS;
    const lote = records.slice(inicio, inicio + IMPORT_MAX_RECORDS);
    onProgresso?.({ enviados: inicio, total: records.length, lote: i + 1, lotes });

    try {
      const resultado = await enviar(lote);
      acumulado = acumulado ? combinar(acumulado, resultado) : resultado;
    } catch (err) {
      // Interrompe, mas devolve o que já entrou: o usuário precisa saber quantos foram gravados
      // antes de tentar de novo, senão reimporta tudo e duplica.
      throw new ErroImportacaoParcial<TResultado>(acumulado, err);
    }
  }

  onProgresso?.({ enviados: records.length, total: records.length, lote: lotes, lotes });
  return acumulado!;
}

export { IMPORT_MAX_RECORDS };
